import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';   
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import DealershipInventory from './pages/DealershipInventory';
import VehicleDetailAdmin from './pages/VehicleDetailAdmin';
import VehiclePacket from './pages/VehiclePacket';
import TradeInEvaluation from './pages/TradeInEvaluation';
import sessionManager from './utils/sessionManager';

interface Vehicle {
  year: string;
  make: string;
  model: string;
  trim: string;
  body: string;
  price: number;
  mileage: number;
  vin: string;
  stockNumber: string;
  imageOne: string;
  imageUrls: string;
  exteriorColor: string;
  interiorColor: string;
  seats: string;
  engine: string;
  drive: string;
  transmission: string;
  fuel: string;
  newUsed: string;
  age: string;
  description: string;
  options: string;
  vehicleUrl: string;
  retail: string;
  invoice: string;
  modelCode: string;
  cost: string;
  location: string;
  vehicleType: string;
  cpo: string;
  mpgCity: string;
  mpgHighway: string;
  vdpLink: string;
  accessories: string;
  saleStatus: string;
  on_lot_since: string;
  cashIncentive: string;
  incentives: string;
  descriptionNoHTML: string;
  modelSeries: string;
  imageLastModified: string;
  certified?: boolean;
}



const isCertified = (vehicle: Vehicle) => {
  if (!vehicle || !vehicle.vdpLink) return false;
  const vdp = vehicle.vdpLink.toLowerCase();
  return (
    vdp.includes('wiersauto.com/certified/gmc') ||
    vdp.includes('wiersauto.com/certified/chevrolet')
  );
};

const isGMC = (make: string | undefined) => make && make.toLowerCase().trim() === 'gmc';

const isUsed = (newUsed: string | undefined) => {
  if (!newUsed) return false;
  const val = newUsed.toLowerCase().trim();
  return val === 'used' || val === 'pre-owned' || val === 'cpo';
};

type DetailsTab = 'home' | 'photos' | 'options' | 'description';

function App() {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [downPayment, setDownPayment] = useState(0);
  const [interestRate, setInterestRate] = useState(5);
  const [loanTerm, setLoanTerm] = useState(60);
  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCalculator, setShowCalculator] = useState(false);
  const [paymentType, setPaymentType] = useState<string>('finance');
  const [calcDownPayment, setCalcDownPayment] = useState(2000);
  const [calcTerm, setCalcTerm] = useState(60);
  const [calcCreditScore, setCalcCreditScore] = useState(700);
  const [includeTaxes, setIncludeTaxes] = useState(false);
  const [detailsTab, setDetailsTab] = useState<DetailsTab>('home');
  const [photoViewer, setPhotoViewer] = useState<{ open: boolean; index: number }>({ open: false, index: 0 });
  const [selectedRebates, setSelectedRebates] = useState<Record<string, boolean>>({});
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [customerFormData, setCustomerFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    zip: '',
    consent: false,
  });
  const [customerFormSuccess, setCustomerFormSuccess] = useState(false);
  const [filter, setFilter] = useState<string | null>(null);
  const [pendingCalculatorVehicle, setPendingCalculatorVehicle] = useState<Vehicle | null>(null);

  // Path-based admin check (after all hooks)
  const [path, setPath] = useState(window.location.pathname);
  const adminLoggedIn = localStorage.getItem('adminLoggedIn') === 'true';

  // Update path when URL changes
  useEffect(() => {
    const handleLocationChange = () => {
      setPath(window.location.pathname);
    };
    
    // Listen for popstate (back/forward button)
    window.addEventListener('popstate', handleLocationChange);
    
    // Initial path set
    setPath(window.location.pathname);
    
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
    };
  }, []);

  // Normalize path (remove trailing slashes, handle variations)
  const normalizedPath = path.replace(/\/+$/, '') || '/';
  
  // Helper function to check if user is properly authenticated
  const isAuthenticated = () => {
    console.log('Checking authentication...');
    
    // Always get fresh value from localStorage to avoid stale state
    const currentAdminLoggedIn = localStorage.getItem('adminLoggedIn') === 'true';
    console.log('adminLoggedIn from localStorage:', currentAdminLoggedIn);
    
    // If not logged in, definitely not authenticated
    if (!currentAdminLoggedIn) {
      console.log('Not logged in');
      return false;
    }
    
    // Check session validity
    const sessionValid = sessionManager.checkSession();
    console.log('Session valid:', sessionValid);
    
    const result = currentAdminLoggedIn && sessionValid;
    console.log('Final authentication result:', result);
    return result;
  };

  // Handle admin route redirects
  useEffect(() => {
    console.log('Current path:', path);
    console.log('Normalized path:', normalizedPath);
    
    if (normalizedPath === '/admin' || normalizedPath === '/admin/dashboard') {
      if (!isAuthenticated()) {
        console.log('Authentication failed in useEffect - redirecting to login');
        localStorage.clear();
        window.location.replace('/admin/login');
      } else if (normalizedPath === '/admin') {
        console.log('Authentication successful in useEffect - redirecting to dashboard');
        window.location.replace('/admin/dashboard');
      }
    }
  }, [path, normalizedPath, adminLoggedIn]);

  // Create loading screen component
  const LoadingScreen = ({ message }: { message: string }) => (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-white text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
        <p>{message}</p>
      </div>
    </div>
  );

  if (normalizedPath === '/admin/login') {
    return <AdminLogin onLogin={() => {}} />;
  }
  if (normalizedPath === '/admin') {
    console.log('Accessing /admin route...');
    console.log('Current localStorage:', {
      adminLoggedIn: localStorage.getItem('adminLoggedIn'),
      sessionStart: localStorage.getItem('sessionStartTime'),
      lastActivity: localStorage.getItem('lastActivity')
    });
    
    // Immediate redirect check - don't rely on useEffect
    if (!isAuthenticated()) {
      console.log('Not authenticated - clearing localStorage and redirecting');
      localStorage.clear();
      window.location.replace('/admin/login');
      return null;
    }
    
    console.log('Authenticated - redirecting to dashboard');
    window.location.replace('/admin/dashboard');
    return null;
  }
  if (normalizedPath === '/admin/dashboard') {
    if (!isAuthenticated()) {
      // Show loading screen while useEffect handles redirect
      return <LoadingScreen message="Redirecting to login..." />;
    }
    return <AdminDashboard />;
  }
  if (normalizedPath.startsWith('/dealership/')) {
    return <DealershipInventory />;
  }
  if (normalizedPath.startsWith('/admin/vehicle/')) {
    return <VehicleDetailAdmin />;
  }
  if (normalizedPath.startsWith('/vehicle-packet/')) {
    return <VehiclePacket />;
  }
  if (normalizedPath.startsWith('/trade-in/')) {
    return <TradeInEvaluation />;
  }

  // Handle root path - should be blocked by nginx but just in case
  if (normalizedPath === '/' || normalizedPath === '/jdpower-test') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <h1 className="text-4xl font-bold mb-4">Site Under Maintenance</h1>
          <p className="text-lg">The main page is temporarily unavailable.</p>
        </div>
      </div>
    );
  }

  // Return 404 for any unknown routes
  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="text-center text-white">
        <h1 className="text-6xl font-bold mb-4">404</h1>
        <h2 className="text-2xl font-semibold mb-4">Page Not Found</h2>
        <p className="text-lg text-gray-300">The page you're looking for doesn't exist.</p>
      </div>
    </div>
  );
}

export default App;